import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import CampusHeader from '../../../components/feature/CampusHeader';
import Footer from '../../../components/feature/Footer';
import { subscribeAuth, signUpWithEmail, getUserRole } from '../../../services/auth';
import { toUserMessage } from '../../../utils/errors';
import { NIGERIAN_UNIVERSITIES } from '../../../data/nigerianUniversities';
import { reload } from 'firebase/auth';

const TOTAL_STEPS = 5;

const SignUpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    university: '',
    referralCode: '',
  });
  const [step, setStep] = useState(0);
  const [slideDir, setSlideDir] = useState(1);
  const [stepError, setStepError] = useState<string | null>(null);
  const [showUniSuggestions, setShowUniSuggestions] = useState(false);

  const uniSuggestions = useMemo(() => {
    const q = form.university.trim().toLowerCase();
    if (!q) return [] as string[];
    return NIGERIAN_UNIVERSITIES
      .filter(u => u.toLowerCase().includes(q))
      .slice(0, 8);
  }, [form.university]);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setForm(prev => ({ ...prev, referralCode: ref }));
    }
  }, [searchParams]);

  useEffect(() => {
    const unsub = subscribeAuth(async (user) => {
      if (user) {
        try { await reload(user); } catch {}
        if (!user.emailVerified) {
          navigate('/auth/email-verification', { replace: true });
          return;
        }
        try {
          const { role } = await getUserRole(user.uid);
          navigate(role === 'seller' ? '/seller-dashboard' : '/buyer-dashboard');
        } catch (e) {
          navigate('/buyer-dashboard');
        }
      }
    });
    return () => unsub();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return 'Password must be at least 8 characters.';
    if (!/\d/.test(password)) return 'Password must contain at least 1 number.';
    if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain at least 1 symbol.';
    return null;
  };

  const validatePhone = (raw: string): string | null => {
    if (!raw.trim()) return 'Please add a phone or WhatsApp number so we can reach you.';
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15) return 'That number doesn’t look right — enter your full phone/WhatsApp number.';
    return null;
  };

  const validateStep = (s: number): string | null => {
    if (s === 0) {
      if (!form.fullName.trim()) return 'Please enter your name so we know who you are.';
      if (form.fullName.trim().length < 2) return 'That name seems a little short — try your full name.';
    }
    if (s === 1) {
      if (!form.email.trim()) return 'We need an email to reach you.';
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return 'That email doesn’t look quite right.';
      const phoneErr = validatePhone(form.phone);
      if (phoneErr) return phoneErr;
    }
    if (s === 2) {
      const err = validatePassword(form.password);
      if (err) return err;
      if (form.password !== form.confirmPassword) return 'Passwords don’t match — try again.';
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) { setStepError(err); return; }
    setStepError(null);
    setSlideDir(1);
    setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const goPrev = () => {
    setStepError(null);
    setSlideDir(-1);
    setStep(s => Math.max(s - 1, 0));
  };

  const skipStep = () => {
    setStepError(null);
    setSlideDir(1);
    setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const passwordStrength = (() => {
    const p = form.password;
    let score = 0;
    if (p.length >= 8) score++;
    if (/\d/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (p.length >= 12) score++;
    return score;
  })();

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    const email = form.email.trim();
    const password = form.password;
    const fullName = form.fullName.trim();
    const phone = form.phone.trim();
    if (!email || !password || !fullName || !phone) {
      setError('Please fill all required fields.');
      return;
    }
    if (password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    const phoneError = validatePhone(phone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    setLoading(true);
    let createdOk = false;
    try {
      await signUpWithEmail(
        email,
        password,
        'buyer',
        {
          fullName,
          university: form.university?.trim() || undefined,
          phone,
          referredByCode: form.referralCode?.trim() || undefined,
        }
      );
      createdOk = true;
      setSuccess('Account created! Redirecting…');
    } catch (err: any) {
      console.error('Signup failed:', err);
      setError(toUserMessage(err));
    } finally {
      setLoading(false);
    }

    if (createdOk) {
      // Navigate outside the try/finally so state updates can't interrupt it.
      // subscribeAuth also navigates as a backup once Firebase fires onAuthStateChanged.
      navigate('/auth/email-verification', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <CampusHeader />
      <main className="max-w-md mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 overflow-hidden">
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs font-medium text-gray-500 mb-2">
              <span>Step {step + 1} of {TOTAL_STEPS}</span>
              <span>{Math.round(((step + 1) / TOTAL_STEPS) * 100)}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={false}
                animate={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
                transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 rounded-full"
              />
            </div>
          </div>

          <div className="relative min-h-[340px]">
            <AnimatePresence mode="wait" custom={slideDir}>
              <motion.div
                key={step}
                custom={slideDir}
                initial={{ x: slideDir > 0 ? 60 : -60, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: slideDir > 0 ? -60 : 60, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 260, damping: 26 }}
                className="space-y-5"
              >
                {step === 0 && (
                  <div className="space-y-5">
                    <div className="text-center">
                      <div className="text-4xl mb-2">👋</div>
                      <h1 className="text-2xl font-bold text-gray-900">Welcome! What’s your name?</h1>
                      <p className="text-gray-500 text-sm mt-1">Let’s set you up with an account.</p>
                    </div>
                    <input
                      type="text"
                      name="fullName"
                      autoFocus
                      value={form.fullName}
                      onChange={handleChange}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goNext(); } }}
                      className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-5">
                    <div className="text-center">
                      <div className="text-4xl mb-2">📬</div>
                      <h1 className="text-2xl font-bold text-gray-900">How can we reach you?</h1>
                      <p className="text-gray-500 text-sm mt-1">Nice to meet you, {form.fullName.split(' ')[0] || 'friend'}!</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        autoFocus
                        value={form.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="your.email@university.edu"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone / WhatsApp number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goNext(); } }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="+234 812 xxx xxxx"
                      />
                      <p className="text-xs text-gray-500 mt-1">We use this to reach you about orders — a WhatsApp number works great.</p>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-5">
                    <div className="text-center">
                      <div className="text-4xl mb-2">🔒</div>
                      <h1 className="text-2xl font-bold text-gray-900">Lock it down</h1>
                      <p className="text-gray-500 text-sm mt-1">Make a password only you would know.</p>
                    </div>
                    <div>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          autoFocus
                          value={form.password}
                          onChange={handleChange}
                          className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Create a strong password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          tabIndex={-1}
                        >
                          <i className={`ri-${showPassword ? 'eye-off-line' : 'eye-line'} text-lg`}></i>
                        </button>
                      </div>
                      <div className="flex gap-1 mt-2">
                        {[0, 1, 2, 3].map(i => (
                          <motion.div
                            key={i}
                            initial={false}
                            animate={{
                              backgroundColor:
                                i < passwordStrength
                                  ? passwordStrength >= 3 ? '#10b981' : passwordStrength === 2 ? '#f59e0b' : '#ef4444'
                                  : '#e5e7eb'
                            }}
                            transition={{ duration: 0.25 }}
                            className="flex-1 h-1.5 rounded-full"
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {passwordStrength === 0 && 'At least 8 characters, 1 number, and 1 symbol.'}
                        {passwordStrength === 1 && 'Getting there…'}
                        {passwordStrength === 2 && 'Decent — add a symbol or more length.'}
                        {passwordStrength === 3 && '💪 Strong password!'}
                        {passwordStrength === 4 && '🔥 Excellent password!'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={form.confirmPassword}
                          onChange={handleChange}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goNext(); } }}
                          className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Type it again"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((s) => !s)}
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          tabIndex={-1}
                        >
                          <i className={`ri-${showConfirmPassword ? 'eye-off-line' : 'eye-line'} text-lg`}></i>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-5">
                    <div className="text-center">
                      <div className="text-4xl mb-2">🎓</div>
                      <h1 className="text-2xl font-bold text-gray-900">Where do you study?</h1>
                      <p className="text-gray-500 text-sm mt-1">Helps us show you listings nearby. Skip if you’d rather not say.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        University <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="university"
                          autoFocus
                          autoComplete="off"
                          value={form.university}
                          onChange={(e) => { handleChange(e); setShowUniSuggestions(true); }}
                          onFocus={() => setShowUniSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowUniSuggestions(false), 150)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Type your school or city — e.g. Lagos, Zaria, OAU"
                        />
                        <AnimatePresence>
                          {showUniSuggestions && uniSuggestions.length > 0 && (
                            <motion.ul
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.15 }}
                              className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-64 overflow-auto"
                            >
                              {uniSuggestions.map((u) => {
                                const q = form.university.trim();
                                const idx = q ? u.toLowerCase().indexOf(q.toLowerCase()) : -1;
                                return (
                                  <li key={u}>
                                    <button
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => {
                                        setForm(prev => ({ ...prev, university: u }));
                                        setShowUniSuggestions(false);
                                      }}
                                      className="w-full text-left px-4 py-2.5 hover:bg-teal-50 text-sm text-gray-700 flex items-center gap-2"
                                    >
                                      <i className="ri-graduation-cap-line text-teal-500 flex-shrink-0"></i>
                                      <span className="truncate">
                                        {idx >= 0 ? (
                                          <>
                                            {u.slice(0, idx)}
                                            <span className="font-semibold text-teal-600">{u.slice(idx, idx + q.length)}</span>
                                            {u.slice(idx + q.length)}
                                          </>
                                        ) : u}
                                      </span>
                                    </button>
                                  </li>
                                );
                              })}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Tip: you can search by city too (e.g. "Lagos", "Zaria").</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Referral code <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        name="referralCode"
                        value={form.referralCode}
                        onChange={(e) => setForm(prev => ({ ...prev, referralCode: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goNext(); } }}
                        className="w-full px-4 py-3 tracking-widest uppercase border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="A friend’s code, if any"
                      />
                      {form.referralCode && (
                        <p className="text-xs text-teal-600 mt-1">🎁 We’ll attribute your signup to this referrer.</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={skipStep}
                      className="w-full text-sm text-gray-500 hover:text-teal-600 underline cursor-pointer"
                    >
                      Skip — I’ll add this later
                    </button>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-5">
                    <div className="text-center">
                      <motion.div
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 14, delay: 0.05 }}
                        className="text-5xl mb-2"
                      >
                        🚀
                      </motion.div>
                      <h1 className="text-2xl font-bold text-gray-900">Ready to go!</h1>
                      <p className="text-gray-500 text-sm mt-1">Quick check, then you’re in.</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Name</span>
                        <span className="font-medium text-gray-900 truncate">{form.fullName}</span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Email</span>
                        <span className="font-medium text-gray-900 truncate">{form.email}</span>
                      </div>
                      {form.phone && (
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">Phone</span>
                          <span className="font-medium text-gray-900 truncate">{form.phone}</span>
                        </div>
                      )}
                      {form.university && (
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">University</span>
                          <span className="font-medium text-gray-900 truncate">{form.university}</span>
                        </div>
                      )}
                      {form.referralCode && (
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">Referral</span>
                          <span className="font-medium text-teal-600 truncate">{form.referralCode}</span>
                        </div>
                      )}
                    </div>
                    <AnimatePresence>
                      {error && (
                        <motion.div
                          key={error}
                          initial={{ opacity: 0, y: -8, x: 0 }}
                          animate={{ opacity: 1, y: 0, x: [0, -8, 8, -6, 6, -3, 3, 0] }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ x: { duration: 0.45 }, opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
                          className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5"
                        >
                          <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0 mt-0.5"></i>
                          <span className="font-medium">{error}</span>
                        </motion.div>
                      )}
                      {success && (
                        <motion.div
                          key={success}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ type: 'spring', stiffness: 280, damping: 20 }}
                          className="flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5"
                        >
                          <i className="ri-check-line text-green-600 text-base flex-shrink-0 mt-0.5"></i>
                          <span className="font-medium">{success}</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <button
                      type="button"
                      onClick={() => handleSubmit()}
                      disabled={loading}
                      className="w-full px-4 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl hover:opacity-95 transition-opacity font-semibold shadow-lg shadow-teal-500/30 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Creating your account…' : 'Create my account 🎉'}
                    </button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <AnimatePresence>
            {stepError && (
              <motion.div
                key={stepError}
                initial={{ opacity: 0, y: -8, x: 0 }}
                animate={{ opacity: 1, y: 0, x: [0, -8, 8, -6, 6, -3, 3, 0] }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ x: { duration: 0.45 }, opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
                className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5"
              >
                <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0 mt-0.5"></i>
                <span className="font-medium">{stepError}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {step < TOTAL_STEPS - 1 && (
            <div className="flex items-center justify-between gap-3 mt-6">
              <button
                type="button"
                onClick={goPrev}
                disabled={step === 0}
                className="px-4 py-2.5 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={goNext}
                className="px-6 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium shadow-md shadow-teal-500/20 cursor-pointer"
              >
                Next →
              </button>
            </div>
          )}

          {step === TOTAL_STEPS - 1 && (
            <div className="flex items-center justify-start mt-4">
              <button
                type="button"
                onClick={goPrev}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                ← Edit details
              </button>
            </div>
          )}

          <p className="text-center text-xs text-gray-500 mt-6">
            Already have an account?{' '}
            <button onClick={() => navigate({ pathname: location.pathname, search: '?login=1' })} className="text-blue-600 hover:text-blue-700 cursor-pointer">Sign In</button>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SignUpPage;
